/* Manchester · PLP card — carrusel por color + Quick Add
   =========================================================================
   Reemplaza: manchester-card-swatch-swap.js y manchester-quickview.js

   POR QUE EXISTE
   --------------
   1) CARRUSEL. product-block.liquid solo renderiza 2 imagenes por tarjeta
      (primary + hover) por una optimizacion de payload. Symmetry navega con
      modulo sobre ese array de 2, asi que las flechas nunca alcanzan la foto
      3+ y el estado --show-on-hover se reasigna en cada click, peleando con
      el hover del usuario. Sintoma: "se queda en la foto de espaldas".

   2) COLOR. Cada color solo enlaza UNA featured_image via variantes, pero en
      el media del producto las fotos de un color van en bloque CONTIGUO
      (verificado: NEGRO 0-7, MARINO 8-14, BLANCO 15-22...). Agrupamos
      cortando entre anclas. No parseamos nombres de archivo: solo 6 de 51
      imagenes siguen el patron SKU_NN, el resto usa el handle.

   PERFORMANCE
   -----------
   Cero bytes extra en el HTML de la PLP. Los datos se piden a
   /products/{handle}.js en la primera interaccion real (hover con intencion
   o tap) y se cachean por handle. Todo por event delegation en document, asi
   que sobrevive a filtros AJAX, sort y paginacion sin re-inicializar nada.
   ========================================================================= */
(function () {
  'use strict';

  var CARD = 'product-block, .product-block';
  // Umbral inline vs selector. Medido en la PLP real (2026-08-14): con la card
  // de mobile a 375px la fila util es de 299px y los chips miden 44px (target
  // tactil). Hasta 6 chips entran en UNA fila de 44px; con 7 u 8 saltan a dos
  // filas (93px) y el bloque tapa casi un quinto de la foto. En desktop (card de
  // 427px) 8 si caben en una fila, pero el umbral es compartido y manda el caso
  // mas angosto. De 7 en adelante conviene el selector compacto.
  var MAX_INLINE_SIZES = 6;
  var HOVER_INTENT_MS = 120;

  var cache = Object.create(null);   // handle -> modelo
  var inflight = Object.create(null);// handle -> promise
  var state = new WeakMap();         // card -> { color, idx, manual, model }

  // Se consulta en vivo, no se congela al cargar: el usuario puede rotar o
  // redimensionar, y en híbridos el valor cambia.
  function isTouch() { return window.matchMedia('(hover: none)').matches; }

  // UX movil: en telefono TODAS las tarjetas usan el mismo punto de entrada
  // ("Seleccionar talla"), tanto polos como camisas de vestir. La complejidad
  // aparece solo despues del tap. Se decide por capacidad de puntero + el
  // breakpoint movil del theme (767.98px), no por user-agent, y se consulta en
  // vivo para que rotar o redimensionar reevalue.
  var MQ_MOBILE = '(hover: none), (max-width: 767.98px)';
  function isMobileUX() { return window.matchMedia(MQ_MOBILE).matches; }

  /* ------------------------------------------------------------------ util */

  function cardOf(el) { return el ? el.closest(CARD) : null; }

  function handleOf(card) {
    if (card.dataset.productHandle) return card.dataset.productHandle;
    var a = card.querySelector('a.product-link[href*="/products/"]');
    if (!a) return null;
    var m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function money(cents) {
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: 'MXN', minimumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }

  /* --------------------------------------------------------------- modelo */

  // Agrupa las imagenes por color usando las anclas featured_image de cada
  // color y cortando en bloques contiguos.
  function buildModel(p) {
    var images = (p.images || []).map(function (src) { return src; });
    var fileIndex = {};
    images.forEach(function (src, i) {
      fileIndex[src.split('?')[0].split('/').pop()] = i;
    });

    var colorPos = 0, sizePos = 1;
    (p.options || []).forEach(function (o, i) {
      var name = (typeof o === 'string' ? o : o.name || '').toUpperCase();
      if (name.indexOf('COLOR') === 0) colorPos = i;
      else if (name.indexOf('TALLA') === 0 || name.indexOf('SIZE') === 0) sizePos = i;
    });
    var optKeys = ['option1', 'option2', 'option3'];
    var colorKey = optKeys[colorPos], sizeKey = optKeys[sizePos];
    var hasColor = (p.options || []).length > 1 || colorPos !== sizePos;

    var colors = [];      // [{name, anchor}]
    var seen = {};
    (p.variants || []).forEach(function (v) {
      var c = hasColor ? v[colorKey] : '__single__';
      if (c == null) c = '__single__';
      if (seen[c] !== undefined) return;
      var anchor = -1;
      if (v.featured_image && v.featured_image.src) {
        var f = v.featured_image.src.split('?')[0].split('/').pop();
        if (fileIndex[f] !== undefined) anchor = fileIndex[f];
      }
      seen[c] = colors.length;
      colors.push({ name: c, anchor: anchor });
    });

    // Orden por posicion real en el media; los colores sin ancla van al final
    var anchored = colors.filter(function (c) { return c.anchor >= 0; })
      .sort(function (a, b) { return a.anchor - b.anchor; });

    var media = {};
    anchored.forEach(function (c, i) {
      var next = anchored[i + 1] ? anchored[i + 1].anchor : images.length;
      media[c.name] = images.slice(c.anchor, next);
    });
    // Color sin ancla (no tiene featured_image en ninguna variante; caso real:
    // BEIGE en el polo, NEGRO/VINO en la camisa Mao). NO caemos a la galeria
    // completa: eso mezclaria fotos de otros colores, que es justo lo que no
    // debe pasar. Le damos una sola foto para que la tarjeta no quede vacia y
    // la navegacion simplemente se desactiva (length < 2).
    colors.forEach(function (c) {
      if (!media[c.name] || !media[c.name].length) {
        media[c.name] = images.length ? [images[0]] : [];
      }
    });

    // Tallas por color, con variant id y disponibilidad reales
    var sizes = {};
    (p.variants || []).forEach(function (v) {
      var c = hasColor ? (v[colorKey] || '__single__') : '__single__';
      var t = v[sizeKey] || v.title;
      (sizes[c] = sizes[c] || []).push({
        label: String(t),
        id: v.id,
        available: !!v.available,
        price: v.price
      });
    });

    return {
      handle: p.handle,
      title: p.title,
      url: '/products/' + p.handle,
      colors: colors.map(function (c) { return c.name; }),
      media: media,
      sizes: sizes,
      images: images,
      // Crudos para que el sistema de tallas compartido resuelva la variante
      // exacta con las MISMAS reglas que el PDP.
      variants: p.variants || [],
      sizeIndex: sizePos,
      colorIndex: colorPos,
      hasColor: hasColor
    };
  }

  function loadProduct(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    if (inflight[handle]) return inflight[handle];
    inflight[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (p) {
        cache[handle] = buildModel(p);
        delete inflight[handle];
        return cache[handle];
      })
      .catch(function (e) { delete inflight[handle]; throw e; });
    return inflight[handle];
  }

  /* ------------------------------------------------------- tallas: parsing */

  // Las camisas de vestir usan TALLA = "CUELLO-MANGA" (14.5-2, 16-4, 17.5-5).
  // Es estructura real de la tienda, no inventada: si TODAS las tallas del
  // color siguen ese patron, presentamos dos pasos en vez de 25 botones.
  // Camisa de vestir: se delega TODO al sistema compartido (mismas reglas que
  // el PDP y los mismos grupos del metafield). Devuelve el arbol
  // Talla sport -> Cuello -> Manga ya filtrado al color activo, o null si el
  // producto no usa el sistema tecnico.
  function dressTree(card) {
    var S = window.MfgSizeSystem;
    if (!S) return null;
    var st_ = st(card);
    if (!st_.model) return null;
    var m = st_.model;
    var color = st_.color || m.colors[0];
    var list = m.sizes[color] || [];
    if (!S.usesDressShirtSizing(list.map(function (x) { return x.label; }))) return null;
    if (!S.getGroups().length) return null;

    var base = [];
    if (m.hasColor && color !== '__single__') base[m.colorIndex] = color;
    var tree = S.buildTree({
      variants: m.variants,
      sizeIndex: m.sizeIndex,
      baseOptions: base,
      groups: S.getGroups()
    });
    return tree.length ? tree : null;
  }

  /* ----------------------------------------------------------- carrusel UI */

  function primaryImg(card) {
    var el = card.querySelector('.product-block__image--primary img');
    return el || card.querySelector('.product-block__image img');
  }

  // Tomamos el control del <img> principal. Neutralizamos el sistema de
  // clases active/hover de Symmetry para que no pelee con la navegacion.
  // Las URLs que vienen de /products/{handle}.js son los archivos MAESTROS (sin
  // parametro de ancho): pintarlas tal cual bajaria imagenes de 2000px+ en la
  // PLP. Pedimos al CDN el ancho que realmente ocupa la tarjeta, redondeado a
  // los escalones tipicos para aprovechar cache.
  var STEPS = [180, 360, 540, 720, 900, 1080];

  function sized(src, cssWidth) {
    if (!src) return src;
    var need = Math.ceil(cssWidth * (window.devicePixelRatio || 1));
    var w = STEPS.find(function (s) { return s >= need; }) || STEPS[STEPS.length - 1];
    try {
      var u = new URL(src, location.origin);
      u.searchParams.set('width', w);
      return u.toString();
    } catch (e) { return src; }
  }

  // Tomar control del <img>. Symmetry monta DOS capas y en hover baja a
  // opacity:0 la capa --active para revelar la --show-on-hover. Si nosotros
  // pintamos una sola capa y escondemos la otra, ese cross-fade deja las dos
  // invisibles y la tarjeta se ve BLANCA — y como las flechas solo aparecen en
  // hover, pasaba en cada click. .mfg-owned anula esa capa (ver CSS).
  // Se marca solo cuando el usuario navega de verdad: hasta entonces el hover
  // nativo sigue funcionando como siempre.
  function own(card) {
    var cont = card.querySelector('.image-cont');
    if (cont) cont.classList.add('mfg-owned');
  }

  function isOwned(card) {
    var cont = card.querySelector('.image-cont');
    return !!(cont && cont.classList.contains('mfg-owned'));
  }

  function paint(card, src) {
    var img = primaryImg(card);
    if (!img || !src) return;
    own(card);

    // El onSelectSwatch nativo de Symmetry (main.js) sigue escuchando el click
    // en los swatches — no lo cortamos porque tambien reescribe el href con
    // ?variant= — y mueve la clase --active entre las dos capas. Si --active
    // acaba en la capa secundaria (que nosotros ocultamos), el estado queda
    // incoherente. La forzamos de vuelta a la capa que realmente pintamos.
    var layer = img.closest('.product-block__image');
    var layers = card.querySelectorAll('.product-block__image');
    for (var i = 0; i < layers.length; i++) {
      layers[i].classList.toggle('product-block__image--active', layers[i] === layer);
      layers[i].classList.remove('product-block__image--inactivated');
    }

    // El ancho se fija UNA vez por tarjeta: si lo recalculamos en cada pintada,
    // clientWidth varia (layout, aparicion de la barra) y la misma foto termina
    // pidiendose con width distinto, duplicando descargas y rompiendo cache.
    var w = parseInt(card.dataset.mfgW || '0', 10);
    if (!w) {
      w = img.clientWidth || card.clientWidth || 400;
      card.dataset.mfgW = w;
    }
    img.srcset = '';
    img.sizes = '';
    img.src = sized(src, w);
  }

  function st(card) {
    var s = state.get(card);
    if (!s) { s = { color: null, idx: 0, manual: false, model: null }; state.set(card, s); }
    return s;
  }

  function currentMedia(card) {
    var s = st(card);
    if (!s.model) return [];
    var c = s.color || s.model.colors[0];
    return s.model.media[c] || s.model.images || [];
  }

  function go(card, delta) {
    var list = currentMedia(card);
    if (list.length < 2) return;
    var s = st(card);
    s.manual = true;                             // a partir de aqui el hover no manda
    s.idx = ((s.idx + delta) % list.length + list.length) % list.length;
    paint(card, list[s.idx]);
    updateArrows(card, list.length);
  }

  function updateArrows(card, total) {
    var wrap = card.querySelector('.image-cont');
    if (!wrap) return;
    wrap.classList.toggle('mfg-has-pages', total > 1);
  }

  /* Manchester · 2026-08-27 — Marca del color activo.
     El script retirado el 2026-08-14 (manchester-card-swatch-swap.js) ponía
     .is-plp-selected + aria-pressed sobre el swatch elegido; este reemplazo
     no heredó esa parte, así que el usuario cambiaba de color y nada le decía
     cuál estaba viendo. El anillo ya está definido en CSS (plp-badges.css y
     manchester-plp-card.css): lo único que faltaba era la clase.
     aria-pressed se toca SOLO donde el markup ya lo declaró, para no inventar
     un rol de botón en los swatches de texto/talla. */
  function markSelectedSwatch(card, colorName) {
    var items = card.querySelectorAll('.product-block-options__item');
    var url = null, sel = null;
    for (var i = 0; i < items.length; i++) {
      var on = items[i].dataset.optionItem === colorName;
      items[i].classList.toggle('is-plp-selected', on);
      if (items[i].hasAttribute('aria-pressed')) {
        items[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      if (on) {
        sel = items[i];
        if (items[i].dataset.plpUrl) url = items[i].dataset.plpUrl;
      }
    }

    // Con la fila en una sola linea el color activo puede caer fuera de vista:
    // al hidratar, la tarjeta salta al primer color COMPRABLE (ver hydrate) y
    // ese puede ser el noveno de catorce. Desplaza la tira, nunca la pagina.
    if (sel) revealSwatch(sel);

    // Manchester · 2026-08-28 — El enlace de la tarjeta pasa a apuntar a la
    // variante del color elegido. `data-plp-url` ya lo emite el Liquid como
    // `<url>?variant=<id>`, la unica forma que el PDP entiende: `?COLOR=<nombre>`
    // —lo que escribian Symmetry y theme.js— lo ignora y abre el color por
    // defecto. Desde que el PLP renombra `data-media` a `data-mfg-media` esos
    // dos handlers ya no se registran aqui, asi que esta es la unica escritura
    // del href y no hay nada con que competir.
    if (url) {
      var links = card.querySelectorAll('a.product-link, a.quickbuy-toggle');
      for (var j = 0; j < links.length; j++) links[j].setAttribute('href', url);
    }
  }

  function setColor(card, colorName) {
    var s = st(card);
    if (!s.model) return;
    s.color = colorName;
    s.idx = 0;
    s.manual = false;
    var list = currentMedia(card);
    if (list.length) paint(card, list[0]);
    updateArrows(card, list.length);
    renderQuickAdd(card);                        // tallas del color nuevo
    markSelectedSwatch(card, colorName);
    setColorName(card, colorName);               // texto bajo el titulo
  }

  // Manchester - 2026-08-30 - El nombre del color visible. Cuelga de setColor,
  // que ya es el unico embudo del estado de la tarjeta: no se crea un segundo
  // sistema ni se vuelve a leer el producto. Si la tarjeta no emite el nodo
  // (producto sin opcion de color) no hace nada.
  // Title Case SOLO de presentacion: `colorName` sigue siendo el valor real de
  // la opcion en todo lo demas (imagen, swatch marcado, href, tallas, carrito).
  // Se separa por espacio, guion y barra para que "GRIS/AZUL" no quede a medias.
  function titleCase(txt) {
    return String(txt).toLocaleLowerCase('es').replace(/[^\s\-\/]+/g, function (w) {
      return w.charAt(0).toLocaleUpperCase('es') + w.slice(1);
    });
  }

  function setColorName(card, colorName) {
    var n = card.querySelector('[data-plp-color-name]');
    if (n && colorName) n.textContent = titleCase(colorName);
  }

  /* ---------------------------------------------------------- Quick Add UI */

  function mount(card) {
    var host = card.querySelector('[data-mfg-qa]');
    if (host) return host;
    var cont = card.querySelector('.image-cont');
    if (!cont) return null;

    host = document.createElement('div');
    host.className = 'mfg-qa';
    host.setAttribute('data-mfg-qa', '');
    cont.appendChild(host);
    return host;
  }

  // Los badges cambian al elegir color (plp-badges se reemplaza por color), asi
  // que esto se re-evalua en cada render y no solo al montar.
  function syncBadgeOffset(card, host) {
    // Manchester · 2026-08-27 — La barra sólo tiene que esquivar la etiqueta si
    // la etiqueta está SOBRE LA FOTO. Desde que existe la posición 'in-detail'
    // (Theme Editor → Badges PLP) el contenedor vive en el bloque de texto, y
    // buscarlo en toda la tarjeta hacía que la barra se desplazara 30px hacia
    // arriba para esquivar algo que ya no está ahí. Se acota a .image-cont, así
    // el desplazamiento vuelve solo si el merchant devuelve la etiqueta a la foto.
    var b = card.querySelector('.image-cont [data-plp-badges]');
    var has = !!(b && b.textContent.trim());
    host.classList.toggle('mfg-qa--above-badges', has);
  }

  function chip(item) {
    return '<button type="button" class="mfg-qa__size" data-mfg-size="' + item.id + '"' +
      (item.available ? '' : ' disabled aria-disabled="true"') +
      ' aria-label="Talla ' + item.label + (item.available ? '' : ' agotada') + '">' +
      item.label + '</button>';
  }

  function renderQuickAdd(card) {
    var s = st(card);
    if (!s.model) return;
    var host = mount(card);
    if (!host) return;
    syncBadgeOffset(card, host);

    var color = s.color || s.model.colors[0];
    var list = (s.model.sizes[color] || []).slice();
    if (!list.length) { host.innerHTML = ''; return; }

    var buyable = list.filter(function (i) { return i.available; });
    if (!buyable.length) {
      host.innerHTML = '<div class="mfg-qa__soldout">Agotado en este color</div>';
      host.classList.add('is-ready');
      return;
    }

    // DESKTOP: camisa de vestir -> selector de 3 pasos; pocas tallas -> chips
    // inline sobre la foto (patron Vuori), sin cambios.
    // MOVIL: nunca chips permanentes sobre la tarjeta. Todo entra por
    // "Seleccionar talla" para que el usuario aprenda UNA sola interaccion y
    // la foto quede limpia.
    var tree = dressTree(card);
    if (!isMobileUX() && !tree && list.length <= MAX_INLINE_SIZES) {
      host.innerHTML = '<div class="mfg-qa__row" role="group" aria-label="Elegir talla">' +
        list.map(chip).join('') + '</div>';
    } else {
      host.innerHTML = '<button type="button" class="mfg-qa__open" data-mfg-open ' +
        'aria-expanded="false">Seleccionar talla</button>';
      host.dataset.mfgMode = tree ? 'dress' : (list.length <= MAX_INLINE_SIZES ? 'simple' : 'grid');
    }
    host.classList.add('is-ready');
  }

  /* -------------------------------------------------------------- overlay */

  var overlay = null;

  function closeOverlay() {
    if (!overlay) return;
    var prev = overlay._returnFocus;
    overlay.remove();
    overlay = null;
    document.body.classList.remove('mfg-qa-locked');
    if (prev && prev.isConnected) prev.focus();
  }

  function openOverlay(card, trigger) {
    closeOverlay();
    var s = st(card);
    var color = s.color || s.model.colors[0];
    var list = s.model.sizes[color] || [];
    var tree = dressTree(card);

    var body;
    var compact = false;
    if (tree) {
      // Progresivo: solo se revela el paso siguiente cuando tiene sentido.
      body = '<p class="mfg-sheet__intro">Selecciona tu talla</p>' +
        '<div class="mfg-sheet__step" data-step="1">' +
        '<p class="mfg-sheet__label">1. Talla</p><div class="mfg-sheet__row">' +
        tree.map(function (g) {
          return '<button type="button" class="mfg-sheet__chip" data-acr="' + g.acronym + '"' +
            (g.available ? '' : ' disabled aria-disabled="true"') +
            ' aria-label="Talla ' + g.acronym + (g.available ? '' : ' agotada') + '">' +
            g.acronym + '</button>';
        }).join('') + '</div></div>' +
        '<div class="mfg-sheet__step" data-step="2" hidden>' +
        '<p class="mfg-sheet__label">2. Cuello</p><div class="mfg-sheet__row" data-collars></div></div>' +
        '<div class="mfg-sheet__step" data-step="3" hidden>' +
        '<p class="mfg-sheet__label">3. Manga</p><div class="mfg-sheet__row" data-cuffs></div></div>';
    } else if (list.length <= MAX_INLINE_SIZES) {
      // Producto simple en movil: hoja compacta, solo las tallas. Misma
      // tipografia, mismos estados y mismos targets que el selector complejo
      // para que ambos se lean como el mismo sistema.
      compact = true;
      body = '<p class="mfg-sheet__intro">Selecciona tu talla</p>' +
        '<div class="mfg-sheet__row" role="group" aria-label="Elegir talla">' +
        list.map(chip).join('') + '</div>';
    } else {
      body = '<p class="mfg-sheet__intro">Selecciona tu talla</p>' +
        '<div class="mfg-sheet__row mfg-sheet__row--grid">' +
        list.map(chip).join('') + '</div>';
    }

    overlay = document.createElement('div');
    overlay.className = 'mfg-sheet' + (compact ? ' mfg-sheet--compact' : '');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Seleccionar talla');
    overlay.innerHTML =
      '<div class="mfg-sheet__scrim" data-close></div>' +
      '<div class="mfg-sheet__panel">' +
      '<div class="mfg-sheet__head"><span>' + (color === '__single__' ? 'Talla' : color) + '</span>' +
      '<button type="button" class="mfg-sheet__x" data-close aria-label="Cerrar">&times;</button></div>' +
      body + '</div>';
    overlay._card = card;
    overlay._returnFocus = trigger || null;
    overlay._tree = tree;
    document.body.appendChild(overlay);
    document.body.classList.add('mfg-qa-locked');
    var first = overlay.querySelector('.mfg-sheet__chip:not([disabled]), .mfg-qa__size:not([disabled])');
    if (first) first.focus();
  }

  /* ----------------------------------------------------------------- cart */

  // Reutiliza el mismo contrato que el add-to-cart del PDP (ver ProductForm en
  // main.js): se piden de vuelta las secciones 'page-header' y 'cart-drawer' en
  // el POST y se refrescan con updateFromCartChange. El evento
  // 'theme:open-cart-drawer' NO sirve aqui: su unico listener vive en theme.js
  // (legacy jQuery) que este theme ya no carga, asi que el drawer nunca abria.
  function addToCart(variantId, card, btn) {
    if (!variantId || (btn && btn.dataset.busy)) return;
    if (btn) { btn.dataset.busy = '1'; btn.classList.add('is-loading'); }

    var sections = ['page-header', 'cart-drawer']
      .map(function (s) { return document.querySelector(s); })
      .filter(Boolean);

    var fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', 1);
    if (sections.length) {
      fd.append('sections', sections.map(function (el) { return el.dataset.sectionId; }).join(','));
      fd.append('sections_url', window.location.pathname);
    }

    var addUrl = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add';

    fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (data) {
        if (btn) { btn.classList.remove('is-loading'); btn.classList.add('is-added'); }
        track(card, variantId);

        // Contenido del drawer via section rendering.
        if (data.sections && sections.length === Object.keys(data.sections).length) {
          sections.forEach(function (el) {
            if (typeof el.updateFromCartChange === 'function') {
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
            }
          });
        }

        // Contador del header. Este theme NO monta <sticky-header> (Manchester
        // lo sustituyo por un header propio), asi que el listener de
        // 'on:cart:change' que trae Symmetry nunca se registra y el numerito no
        // se actualiza solo. Lo escribimos nosotros. Igual emitimos el evento
        // por si otro componente lo escucha.
        // El listener global de 'on:cart:change' se encarga del contador.
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));

        closeOverlay();

        // El usuario se queda en la PLP: si el theme esta configurado para
        // redirigir a /cart tras agregar, aqui lo tratamos como drawer.
        var after = (window.theme && theme.settings && theme.settings.afterAddToCart) || 'drawer';
        var drawer = document.querySelector('.js-cart-drawer');
        if (after === 'notification' && document.getElementById('AddedNotification')) {
          var n = document.getElementById('AddedNotification').content.firstElementChild.cloneNode(true);
          n.dataset.productTitle = data.product_title || '';
          (document.querySelector('.pageheader--sticky') || document.body).appendChild(n);
        } else if (drawer && typeof drawer.open === 'function') {
          drawer.open(btn && btn.isConnected ? btn : undefined);
        }

        document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true }));

        setTimeout(function () {
          if (btn) { btn.classList.remove('is-added'); delete btn.dataset.busy; }
        }, 1400);
      })
      .catch(function () {
        if (btn) { btn.classList.remove('is-loading'); delete btn.dataset.busy; }
        flash(card, 'No se pudo agregar. Intenta de nuevo.');
      });
  }

  // El span .cart-link__count se renderiza siempre (ver sections/header.liquid);
  // vacio queda oculto por CSS via :empty.
  function refreshCartCount() {
    var el = document.querySelector('.cart-link__count');
    if (!el) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (c) { el.textContent = c.item_count > 0 ? c.item_count : ''; })
      .catch(function () { /* el drawer ya muestra el estado real */ });
  }

  function flash(card, msg) {
    var host = card.querySelector('[data-mfg-qa]');
    if (!host) return;
    var n = document.createElement('div');
    n.className = 'mfg-qa__error';
    n.setAttribute('role', 'status');
    n.textContent = msg;
    host.appendChild(n);
    setTimeout(function () { n.remove(); }, 2600);
  }

  function track(card, variantId) {
    try {
      var s = st(card);
      if (!s.model) return;
      var color = s.color || s.model.colors[0];
      var item = (s.model.sizes[color] || []).find(function (x) {
        return String(x.id) === String(variantId);
      });
      (window.dataLayer = window.dataLayer || []).push({
        event: 'quick_add_to_cart',
        source: 'plp_quick_add',
        currency: 'MXN',
        value: item ? item.price / 100 : null,
        items: [{
          item_name: s.model.title,
          item_variant: (color === '__single__' ? '' : color + ' / ') + (item ? item.label : ''),
          price: item ? item.price / 100 : null,
          quantity: 1
        }]
      });
    } catch (e) { /* analytics nunca bloquea la compra */ }
  }

  /* ------------------------------------------------------------ hidratacion */

  function hydrate(card) {
    var s = st(card);
    if (s.model || s.loading) return Promise.resolve(s.model);
    var handle = handleOf(card);
    if (!handle) return Promise.resolve(null);
    s.loading = true;
    return loadProduct(handle).then(function (model) {
      s.loading = false;
      s.model = model;
      if (!s.color) {
        // Manchester - 2026-08-31 - El color inicial lo decide el SERVIDOR.
        //
        // Orden de precedencia:
        //   1. swatch ya marcado  -> el usuario ya eligio en esta sesion;
        //   2. data-plp-color     -> el color que Liquid resolvio para ESTA vista,
        //      que es el mismo que pinto la imagen, el que dice la etiqueta y el
        //      que apunta el href ?variant=. Bajo filtro de color es un color
        //      ACTIVO, porque sale de la variante que Shopify hizo casar;
        //   3. model.colors[0]    -> ultimo recurso, sin filtros de por medio.
        //
        // El paso 2 es la correccion: model.colors se arma recorriendo p.variants
        // en orden de catalogo y NO sabe nada del filtro vigente, asi que como
        // semilla podia meter un color que el usuario acaba de excluir. En
        // MA036122-42 bajo CELESTE+GRIS CLARO colors[0] es ROSA.
        var sel = card.querySelector('.product-block-options__item.is-plp-selected');
        var name = sel && sel.dataset.optionItem;
        if (!(name && model.media[name])) {
          var srv = card.querySelector('[data-plp-color]');
          var raw = srv && srv.getAttribute('data-plp-color');
          name = (raw && model.media[raw]) ? raw : null;
        }
        s.color = name || model.colors[0];
      }

      // ==================================================================
      // Manchester · 2026-08-28 — El color inicial tiene que ser COMPRABLE.
      //
      // `model.colors` se construye recorriendo p.variants en el orden de
      // Shopify y quedandose con cada color la primera vez que aparece: NO
      // filtra stock. Asi que `model.colors[0]` es el color de la primera
      // variante, este agotado o no.
      //
      // Caso real (MA033122-02, "Camisa de Vestir Lisa"): BLANCO es el primer
      // color y tiene sus 25 tallas en cero, mientras MARINO/VINO/CELESTE/
      // NEGRO suman 21 unidades. La tarjeta abria en BLANCO y el Quick Add
      // decia "Agotado en este color" — pero la fila de swatches ni siquiera
      // mostraba BLANCO, porque el gate de Liquid ya oculta los colores sin
      // stock. Foto de un color, oferta de otros cuatro, y un cartel de
      // agotado: tres estados distintos en la misma tarjeta.
      //
      // Se corrige aqui y no en CSS porque el problema es de ESTADO, no de
      // presentacion. Y se delega en setColor() en vez de tocar s.color a
      // mano: esa funcion ya sincroniza imagen, swatch activo, aria-pressed,
      // href ?variant=, flechas y tallas desde una sola fuente. Si no hay
      // ningun color comprable (producto agotado por completo) no se fuerza
      // nada y la tarjeta cae al mensaje de agotado, que ahi si corresponde.
      // ==================================================================
      var comprable = function (c) {
        var tallas = model.sizes[c] || [];
        for (var t = 0; t < tallas.length; t++) { if (tallas[t].available) return true; }
        return false;
      };
      if (!comprable(s.color)) {
        for (var i = 0; i < model.colors.length; i++) {
          if (comprable(model.colors[i])) { setColor(card, model.colors[i]); return model; }
        }
      }

      updateArrows(card, currentMedia(card).length);
      renderQuickAdd(card);
      return model;
    }).catch(function () { s.loading = false; return null; });
  }

  /* -------------------------------------------------------------- eventos */

  var hoverTimer = null;

  document.addEventListener('mouseover', function (e) {
    if (isTouch()) return;
    var card = cardOf(e.target);
    if (!card) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () { hydrate(card); }, HOVER_INTENT_MS);

    // Manchester · 2026-08-28 — Vista previa del color al pasar por encima.
    // La hacia onSelectSwatch de Symmetry; al renombrar data-media en el PLP ese
    // handler deja de registrarse, asi que la absorbemos aqui. Se cuelga del
    // mouseover que ya existia: ningun listener nuevo, ningun timer nuevo.
    // Solo pinta; NO toca estado, seleccion ni href — eso es exclusivo del click,
    // que es justo lo que evita que un simple hover corrompa el enlace.
    var sw = e.target.closest && e.target.closest('.product-block-options__item');
    if (!sw || !sw.dataset.optionItem) return;
    var s = st(card);
    if (!s.model) return;                       // aun sin hidratar: no hay nada que pintar
    var list = s.model.media[sw.dataset.optionItem];
    if (list && list.length) paint(card, list[0]);
  });

  document.addEventListener('mouseout', function (e) {
    var card = cardOf(e.target);
    if (!card) return;
    if (e.relatedTarget && cardOf(e.relatedTarget) === card) return;
    clearTimeout(hoverTimer);
    var s = st(card);
    // Al salir de la tarjeta volvemos a la foto 1 del color activo, pero solo
    // si ya controlamos la imagen (el usuario eligio color) y no navego a mano.
    // Si aun no la controlamos no tocamos nada: el cross-fade nativo del theme
    // sigue mandando y no le robamos el comportamiento sin motivo.
    if (s.model && !s.manual && isOwned(card)) {
      var list = currentMedia(card);
      if (list.length) { s.idx = 0; paint(card, list[0]); }
    }
  });

  // Delegacion de clicks EN FASE DE CAPTURA.
  // Verificado en la tienda: un listener intermedio llama stopPropagation() en
  // los clicks dentro de la tarjeta, asi que en burbuja este handler nunca se
  // ejecutaba (capture=1, bubble=0) — y por eso tampoco corria el paginador
  // nativo de Symmetry. En captura corremos antes de que nadie corte el evento.
  // Solo detenemos la propagacion en los elementos que son nuestros (flechas,
  // tallas, overlay); el swatch se deja pasar para no romper el handler de
  // Symmetry que reescribe el href con ?variant=.
  document.addEventListener('click', function (e) {
    // Primero las flechas de color: el boton vive DENTRO de la fila y no
    // queremos que ningun otro handler lo interprete.
    var swNav = e.target.closest && e.target.closest('.mfg-swrow__nav');
    if (swNav) {
      e.preventDefault();
      e.stopPropagation();
      if (!swNav.disabled) nudgeRow(swNav.closest('mfg-swatch-row'), swNav.classList.contains('mfg-swrow__nav--next') ? 1 : -1);
      return;
    }

    var arrow = e.target.closest('.image-page-button');
    if (arrow) {
      var card = cardOf(arrow);
      if (!card) return;
      e.preventDefault();
      e.stopPropagation();
      var dir = arrow.classList.contains('image-page-button--next') ? 1 : -1;
      var s = st(card);
      if (!s.model) { hydrate(card).then(function () { go(card, dir); }); }
      else go(card, dir);
      return;
    }

    // Swatch de color
    var sw = e.target.closest('.product-block-options__item');
    if (sw && sw.dataset.optionItem) {
      var c2 = cardOf(sw);
      if (c2) {
        var name = sw.dataset.optionItem;
        var s2 = st(c2);
        if (!s2.model) hydrate(c2).then(function () { setColor(c2, name); });
        else setColor(c2, name);
      }
      return; // no hacemos preventDefault: swatch-swap legacy ya lo maneja
    }

    // Abrir selector de tallas
    var open = e.target.closest('[data-mfg-open]');
    if (open) {
      e.preventDefault();
      e.stopPropagation();
      var c3 = cardOf(open);
      if (!c3) return;
      open.setAttribute('aria-expanded', 'true');
      openOverlay(c3, open);
      try {
        (window.dataLayer = window.dataLayer || []).push({ event: 'quick_add_open' });
      } catch (err) { /* noop */ }
      return;
    }

    // Chip de talla (inline o dentro del overlay)
    var size = e.target.closest('.mfg-qa__size');
    if (size && !size.disabled) {
      e.preventDefault();
      e.stopPropagation();
      var c4 = overlay && overlay.contains(size) ? overlay._card : cardOf(size);
      if (!c4) return;
      try {
        (window.dataLayer = window.dataLayer || []).push({ event: 'size_select' });
      } catch (err) { /* noop */ }
      addToCart(size.dataset.mfgSize, c4, size);
      return;
    }

    // Paso 1: Talla sport -> revela cuellos de ese grupo
    var acr = e.target.closest('[data-acr]');
    if (acr && overlay && !acr.disabled) {
      e.preventDefault();
      var tr = overlay._tree;
      if (!tr) return;
      var grp = tr.find(function (g) { return g.acronym === acr.dataset.acr; });
      if (!grp) return;
      overlay._grp = grp;
      overlay.querySelectorAll('[data-acr]').forEach(function (b) {
        b.classList.toggle('is-active', b === acr);
      });
      var cRow = overlay.querySelector('[data-collars]');
      cRow.innerHTML = grp.collars.map(function (c) {
        return '<button type="button" class="mfg-sheet__chip" data-collar="' + c.collar + '"' +
          (c.available ? '' : ' disabled aria-disabled="true"') +
          ' aria-label="Cuello ' + c.collar + ' pulgadas' + (c.available ? '' : ' agotado') + '">' +
          c.collar + '"</button>';
      }).join('');
      overlay.querySelector('[data-step="2"]').hidden = false;
      // Elegir otra talla invalida el paso 3.
      overlay.querySelector('[data-step="3"]').hidden = true;
      overlay.querySelector('[data-cuffs]').innerHTML = '';
      var fc = cRow.querySelector('button:not([disabled])');
      if (fc) fc.focus();
      return;
    }

    // Paso 2: Cuello -> revela mangas (en pulgadas, como el PDP)
    var col = e.target.closest('[data-collar]');
    if (col && overlay && !col.disabled) {
      e.preventDefault();
      var g2 = overlay._grp;
      if (!g2) return;
      var entry = g2.collars.find(function (c) { return c.collar === col.dataset.collar; });
      if (!entry) return;
      overlay.querySelectorAll('[data-collar]').forEach(function (b) {
        b.classList.toggle('is-active', b === col);
      });
      var sRow = overlay.querySelector('[data-cuffs]');
      sRow.innerHTML = entry.cuffs.map(function (x) {
        return '<button type="button" class="mfg-qa__size" data-mfg-size="' + x.variantId + '"' +
          (x.available ? '' : ' disabled aria-disabled="true"') +
          ' aria-label="Manga ' + x.cuffLabel + ' pulgadas' + (x.available ? '' : ' agotada') + '">' +
          x.cuffLabel + '"</button>';
      }).join('');
      overlay.querySelector('[data-step="3"]').hidden = false;
      var fs = sRow.querySelector('button:not([disabled])');
      if (fs) fs.focus();
      return;
    }

    if (e.target.closest('[data-close]')) { e.preventDefault(); closeOverlay(); }
  }, true); // CAPTURA — ver nota abajo

  // Mobile: no hay hover, y no podemos esperar a que el usuario toque la
  // tarjeta porque tocarla lo manda al PDP — nunca veria la barra. La
  // hidratamos cuando entra en pantalla, que es lo que la CSS de touch asume
  // (barra siempre visible). Se hace de a poco y se deja de observar en cuanto
  // se hidrata, para no disparar 34 fetches de golpe al cargar la coleccion.
  var io = null;
  var cola = [];
  var vaciando = false;

  function vaciarCola() {
    if (vaciando || !cola.length) return;
    vaciando = true;
    var card = cola.shift();
    hydrate(card).then(function () {
      vaciando = false;
      if (cola.length) setTimeout(vaciarCola, 60);
    });
  }

  function observarTouch() {
    if (!isTouch() || !('IntersectionObserver' in window)) return;
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          io.unobserve(en.target);
          if (!st(en.target).model) { cola.push(en.target); vaciarCola(); }
        });
      }, { rootMargin: '150px 0px' });
    }
    document.querySelectorAll(CARD).forEach(function (c) {
      if (!c.dataset.mfgObs) { c.dataset.mfgObs = '1'; io.observe(c); }
    });
  }

  /* ------------------------------------------- Reserva de titulo POR FILA
     PROBLEMA: la reserva de 2 lineas es fija, asi que una fila donde TODOS los
     nombres caben en una linea arrastra una linea vacia entre el titulo y el
     color.

     POR QUE NO SE RESUELVE CON CSS: lo natural seria `subgrid`, pero el titulo
     y el precio viven dentro de `a.product-link`, a cuatro niveles del grid;
     habria que convertir en subgrid `.product-block`, `.block-inner`,
     `.product-block__detail`, `.innerer` y el propio enlace. Es reestructurar
     la tarjeta entera para un ajuste de altura.

     COSTE: dos reflows forzados para TODA la rejilla, no uno por tarjeta. Se
     escribe la clase de medicion, se lee todo de golpe, se quita y se escriben
     las variables. Nada de polling, nada de medir en scroll, y ningun observer
     nuevo: se cuelga de los mismos avisos que ya usa observarTouch().

     Las filas se detectan por la posicion real de cada tarjeta (`offsetTop`),
     no por un "cada 2" o "cada 4": si cambia el viewport o el numero de
     columnas, el agrupado sigue siendo correcto. */
  var titulosPendiente = false;
  function ajustarTitulos() {
    titulosPendiente = false;
    var grids = document.querySelectorAll('.product-grid--plp');
    if (!grids.length) return;

    Array.prototype.forEach.call(grids, function (grid) {
      var titulos = grid.querySelectorAll('.product-block__title');
      if (!titulos.length) return;

      grid.classList.add('mfg-midiendo-titulos');           // escritura 1

      var datos = [];                                        // lectura unica
      Array.prototype.forEach.call(titulos, function (t) {
        var card = t.closest('.product-block');
        if (!card) return;
        var lh = parseFloat(getComputedStyle(t).lineHeight) || 1;
        datos.push({ card: card, fila: card.offsetTop, lineas: Math.round(t.offsetHeight / lh) });
      });

      grid.classList.remove('mfg-midiendo-titulos');         // escritura 2

      var porFila = {};
      datos.forEach(function (d) {
        var n = Math.min(2, Math.max(1, d.lineas || 1));
        porFila[d.fila] = Math.max(porFila[d.fila] || 1, n);
      });
      datos.forEach(function (d) {
        var n = String(porFila[d.fila]);
        if (d.card.style.getPropertyValue('--mfg-title-lines') !== n) {
          d.card.style.setProperty('--mfg-title-lines', n);
        }
      });
    });
  }

  function pedirAjusteTitulos() {
    if (titulosPendiente) return;                            // coalescido
    titulosPendiente = true;
    requestAnimationFrame(ajustarTitulos);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      observarTouch(); pedirAjusteTitulos();
    });
  } else {
    observarTouch(); pedirAjusteTitulos();
  }
  // Las fuentes web cambian la metrica del texto: si no se re-mide, una fila
  // puede quedar calculada con la tipografia de respaldo.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(pedirAjusteTitulos);

  // Filtros AJAX / cargar mas reemplazan las tarjetas: volver a observar.
  if (window.MutationObserver) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes.length) { observarTouch(); pedirAjusteTitulos(); return; }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
  window.addEventListener('resize', function () {
    observarTouch(); pedirAjusteTitulos();
  }, { passive: true });

  // Al cruzar el breakpoint (rotar, redimensionar, abrir devtools) las tarjetas
  // ya hidratadas deben cambiar de presentacion: chips inline en desktop,
  // "Seleccionar talla" en movil. Solo se re-pinta el Quick Add; el modelo, el
  // color activo y el carrusel se conservan, asi que no hay fetches nuevos.
  var mqMobile = window.matchMedia(MQ_MOBILE);
  var onBreakpoint = function () {
    document.querySelectorAll(CARD).forEach(function (c) {
      if (st(c).model) renderQuickAdd(c);
    });
    observarTouch();
    pedirAjusteTitulos();
  };
  if (mqMobile.addEventListener) mqMobile.addEventListener('change', onBreakpoint);
  else if (mqMobile.addListener) mqMobile.addListener(onBreakpoint);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay) { e.preventDefault(); closeOverlay(); }

    // El swatch declara role="button" y tabindex="0" desde siempre, pero un
    // <div> con rol de boton NO recibe click al pulsar Enter o Espacio: eso solo
    // lo hace un <button> real. Era enfocable y no activable.
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var t = e.target;
    if (!t || !t.closest) return;
    var sw = t.closest('.product-block-options__item[role="button"]');
    if (!sw || !sw.dataset.optionItem) return;
    e.preventDefault();
    sw.click();
  });

  // Contador del header, a nivel de tienda. No basta con arreglar nuestro
  // Quick Add: el add-to-cart del PDP (ProductForm en main.js) solo refresca la
  // seccion 'cart-drawer' — 'page-header' no existe en este theme — y como
  // tampoco hay <sticky-header>, nadie actualiza el numerito. Verificado en el
  // PDP: se agregaba bien pero el contador se quedaba vacio. Escuchamos los
  // eventos que el propio theme ya emite para cubrir todas las rutas de compra.
  ['on:cart:add', 'on:cart:change', 'on:cart:after-merge'].forEach(function (evt) {
    document.addEventListener(evt, function () { refreshCartCount(); });
  });

  /* ============ FILA DE COLOR EN UNA SOLA LINEA — <mfg-swatch-row> ==========
     El elemento ES el contenedor .product-block-options--swatch del PLP: el
     Liquid solo le cambia el nombre de etiqueta, asi que no anade ni un nodo.

     Por que un custom element y no un MutationObserver: el resto del archivo va
     por delegacion en `document` y sobrevive a los filtros AJAX sin re-init,
     pero esto hay que MEDIRLO (si la tira desborda) y una medicion no se
     delega. connectedCallback() es la unica senal nativa de "esta tarjeta entro
     al DOM" —render inicial, section rendering, sort, paginacion—, el navegador
     la llama una vez por instancia (imposible duplicar listeners) y no obliga a
     observar el documento entero.

     Coste: un ResizeObserver compartido por toda la pagina y un listener de
     scroll pasivo por fila, estrangulado con rAF, que se recolecta con la
     tarjeta. Las flechas solo se construyen con overflow y puntero fino.
     ====================================================================== */

  var OVER = 'mfg-swrow--over';
  var mqFine = window.matchMedia('(hover: hover) and (min-width: 768px)');
  var mqCalm = window.matchMedia('(prefers-reduced-motion: reduce)');
  function ease() { return mqCalm.matches ? 'auto' : 'smooth'; }

  var CHEVRON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="DDD"/></svg>';
  var PREV_SVG = CHEVRON.replace('DDD', 'm15 18-6-6 6-6');
  var NEXT_SVG = CHEVRON.replace('DDD', 'm9 18 6-6-6-6');

  // Un solo ResizeObserver para toda la pagina: dispara cuando el ancho util
  // cambia de verdad (rotar, redimensionar, abrir filtros), no en cada mutacion.
  // Lo que escribe el callback -clases y `disabled`- no altera el tamano de lo
  // observado, asi que no puede realimentarse.
  var swRO = null;
  function roGet() {
    if (swRO === null && typeof ResizeObserver !== 'undefined') {
      swRO = new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var row = entries[i].target.parentNode;
          if (row && row.mfgSync) row.mfgSync();
        }
      });
    }
    return swRO;
  }

  function boxOf(row) { return row && row.querySelector('.product-block-options__inner'); }

  function edges(row) {
    var b = boxOf(row);
    if (!b) return;
    var max = b.scrollWidth - b.clientWidth;
    var atStart = b.scrollLeft <= 1;
    var atEnd = b.scrollLeft >= max - 1;
    row.classList.toggle('is-start', atStart);
    row.classList.toggle('is-end', atEnd);
    if (row._prev) row._prev.disabled = atStart;
    if (row._next) row._next.disabled = atEnd;
  }

  function buildNav(row) {
    var p = document.createElement('button');
    p.type = 'button';
    p.className = 'mfg-swrow__nav mfg-swrow__nav--prev';
    p.setAttribute('aria-label', 'Ver colores anteriores');
    p.innerHTML = PREV_SVG;
    var n = document.createElement('button');
    n.type = 'button';
    n.className = 'mfg-swrow__nav mfg-swrow__nav--next';
    n.setAttribute('aria-label', 'Ver más colores');
    n.innerHTML = NEXT_SVG;
    // prev antes de la tira y next despues: el orden del DOM es el de tabulacion.
    row.insertBefore(p, row.firstChild);
    row.appendChild(n);
    row._prev = p;
    row._next = n;
  }

  // Avanza casi una pantalla dejando un circulo de contexto, cuadrado al paso
  // para que no quede medio swatch pegado al borde.
  function nudgeRow(row, dir) {
    var b = boxOf(row);
    if (!b) return;
    var it = b.querySelector('.product-block-options__item');
    var step = it ? Math.round(it.getBoundingClientRect().width) + (parseFloat(getComputedStyle(b).columnGap) || 0) : 30;
    var page = Math.max(step, Math.floor((b.clientWidth - step) / step) * step);
    b.scrollBy({ left: dir * page, behavior: ease() });
  }

  // scrollBy sobre la tira y no scrollIntoView: este ultimo arrastraria tambien
  // el scroll vertical de la pagina.
  function revealSwatch(item) {
    var b = item && item.parentNode;
    if (!b || !b.classList || !b.classList.contains('product-block-options__inner')) return;
    if (b.scrollWidth - b.clientWidth < 2) return;
    var ir = item.getBoundingClientRect();
    var br = b.getBoundingClientRect();
    var d = 0;
    if (ir.left < br.left + 2) d = ir.left - br.left - 2;
    else if (ir.right > br.right - 2) d = ir.right - br.right + 2;
    if (d) b.scrollBy({ left: d, behavior: ease() });
  }

  // MEDICION EN LOTE. Cada fila que entra al DOM necesita saber si desborda, y
  // eso es una lectura de layout. Hacerlo fila por fila -34 callbacks, cada uno
  // leyendo despues de que el anterior escribio- son 34 layouts forzados: medido,
  // +60 ms de TBT con CPU a 4x. Aqui se acumulan las filas pendientes, se LEE
  // todo de una y solo despues se ESCRIBE. Un unico callback para toda la pagina.
  var pend = [], pendId = 0;

  function flushPend() {
    pendId = 0;
    var rows = pend, m = [], i;
    pend = [];
    for (i = 0; i < rows.length; i++) {                 // ---- solo lecturas
      var b = boxOf(rows[i]);
      if (b) m.push([rows[i], b.scrollWidth, b.clientWidth, b.scrollLeft]);
    }
    for (i = 0; i < m.length; i++) {                    // ---- solo escrituras
      var row = m[i][0], max = m[i][1] - m[i][2], sl = m[i][3];
      var over = max > 1, atStart = sl <= 1, atEnd = sl >= max - 1;
      row.classList.toggle(OVER, over);
      if (over && !row._prev && mqFine.matches) buildNav(row);
      row.classList.toggle('is-start', atStart);
      row.classList.toggle('is-end', atEnd);
      if (row._prev) row._prev.disabled = atStart;
      if (row._next) row._next.disabled = atEnd;
    }
  }

  // rAF y NO requestIdleCallback: al marcar `mfg-swrow--over` el rail se estrecha
  // para hacerle hueco a la flecha, y eso mueve los circulos. En idle esa clase
  // aterriza DESPUES del primer pintado -> salto visible y CLS. rAF la aplica
  // antes de pintar. Cubre igual el re-render por filtros, donde un salto
  // despues del pintado tambien contaria como CLS. Coste: una sola pasada por
  // pagina, medida en 6 ms con la CPU a 4x.
  function queueRow(row) {
    pend.push(row);
    if (pendId) return;
    pendId = requestAnimationFrame(flushPend);
  }

  if (window.customElements && !customElements.get('mfg-swatch-row')) {
    customElements.define('mfg-swatch-row', class extends HTMLElement {
      connectedCallback() {
        var box = boxOf(this);
        if (!box) return;
        var self = this;
        if (!this._bound) {
          this._bound = true;
          this.mfgSync = function () { queueRow(self); };
          box.addEventListener('scroll', function () {
            if (self._raf) return;
            self._raf = requestAnimationFrame(function () { self._raf = 0; edges(self); });
          }, { passive: true });

          // DENTRO DE UN CARRUSEL: cortar el arrastre por raton del padre.
          // CarouselSlider engancha mousedown/mousemove en `.slider` con
          // (pointer: fine) y handleMousemove (main.js:3543) desplaza el
          // carrusel con CUALQUIER movimiento, sin umbral -el de 8px solo marca
          // `is-dragging`-. Medido: arrastrar sobre la tira movia el CARRUSEL de
          // 396 a 456 sin mover un solo swatch, y un clic con 11px de temblor lo
          // empujaba 11px. Basta con que el mousedown no llegue al `.slider`:
          // el raton no arrastra scrollers nativos, asi que no se pierde nada.
          // Un solo listener, y solo en las filas que viven en un carrusel.
          if (this.closest('.slider')) {
            this.addEventListener('mousedown', function (e) { e.stopPropagation(); });
          }
        }
        var ro = roGet();
        if (ro) ro.observe(box);
        queueRow(self);
      }
      disconnectedCallback() {
        // Sin esto el ResizeObserver retendria las tarjetas viejas en cada filtrado.
        var ro = swRO, box = boxOf(this);
        if (ro && box) ro.unobserve(box);
      }
    });
  }

  // Al cruzar el breakpoint hacia escritorio hay que construir las flechas de
  // las filas ya montadas (en movil no se crean). Sin listener nuevo de resize.
  var onFine = function () {
    document.querySelectorAll('mfg-swatch-row').forEach(function (r) { if (r.mfgSync) r.mfgSync(); });  // se agrupan en un solo flush
  };
  if (mqFine.addEventListener) mqFine.addEventListener('change', onFine);
  else if (mqFine.addListener) mqFine.addListener(onFine);
})();
